import { useTranslation } from "react-i18next"

import OpenCloseIcon from "@/assets/icons/openClose.svg?react"
import MoogliLogo from "@/assets/logo/platform.svg?react"
import MoogliLogoDark from "@/assets/logo/platformDark.svg?react"
import LanguageSwitcher from "@/components/shared/LanguageSwitcher"
import SupportChatButton from "@/components/shared/SupportChatButton"
import { cn } from "@/lib/utils"

import HeaderNotificationBell from "./HeaderNotificationBell"
import HeaderProfileMenu from "./HeaderProfileMenu"

interface GlobalHeaderProps {
  isNavbarCollapsed: boolean
  onToggleNavbar: () => void
  mobileOpen?: boolean
  onToggleMobileNavbar?: () => void
}

export default function GlobalHeader({
  isNavbarCollapsed,
  onToggleNavbar,
  mobileOpen,
  onToggleMobileNavbar,
}: GlobalHeaderProps) {
  const { t } = useTranslation()

  // `pl-4` keeps the logo aligned with the Navbar, which is also `px-4`. `pr-8`
  // matches the page content below (`px-8`): at `px-4` the avatar and the icons
  // beside it overhung the content's right margin by 16px.
  return (
    <header className="flex h-[72px] shrink-0 items-center justify-between gap-2 px-3 md:pl-4 md:pr-8">
      <button
        type="button"
        className="rounded-lg p-2 md:hidden"
        onClick={onToggleMobileNavbar}
        aria-expanded={mobileOpen}
        aria-label={t("expandSidebar")}
      >
        <OpenCloseIcon className="size-6" />
      </button>
      <div className="hidden shrink-0 items-center gap-2 md:flex md:gap-[60px]">
        <button
          type="button"
          onClick={onToggleNavbar}
          className="group flex shrink-0 items-center rounded-lg p-1 text-neutral-text transition-colors hover:bg-neutral-secondBg hover:text-neutral-title"
          aria-label={isNavbarCollapsed ? t("expandSidebar") : t("collapseSidebar")}
        >
          <span className="relative flex items-center">
            <MoogliLogoDark className="max-w-none shrink-0 transition-opacity duration-300 ease-out group-hover:opacity-0 group-hover:ease-in" />
            <MoogliLogo className="absolute inset-0 max-w-none shrink-0 opacity-0 transition-opacity duration-300 ease-in group-hover:opacity-100 group-hover:ease-out" />
          </span>
        </button>
        {/* Fades out while collapsed — clicking the logo re-opens the sidebar once it's invisible */}
        <button
          type="button"
          onClick={onToggleNavbar}
          tabIndex={isNavbarCollapsed ? -1 : 0}
          aria-hidden={isNavbarCollapsed}
          className={cn(
            "flex shrink-0 items-center rounded-lg p-1 text-neutral-text transition-all duration-300 ease-in-out hover:bg-neutral-secondBg hover:text-neutral-title",
            isNavbarCollapsed ? "pointer-events-none opacity-0" : "opacity-100",
          )}
          aria-label={t("collapseSidebar")}
        >
          <OpenCloseIcon className="h-6 w-6 shrink-0" />
        </button>
      </div>

      <div className="flex min-w-0 items-center gap-2 md:gap-8">
        <div className="flex items-center gap-3 md:gap-12">
          <SupportChatButton />
          <LanguageSwitcher />
          <HeaderNotificationBell />
        </div>
        <div className="mx-2 hidden h-6 w-px bg-neutral-border md:block" aria-hidden />
        <HeaderProfileMenu />
      </div>
    </header>
  )
}
